<template>
  <component
    :is="static ? 'div' : 'button'"
    v-if="relation"
    :type="static ? undefined : 'button'"
    class="client-chip"
    :class="{ 'client-chip--interactive': !static }"
    :title="static ? undefined : $t('Open client')"
    :data-test="dataTest"
    @click="open"
  >
    <AvatarWithFallback
      :id="relation.client.id"
      :avatar="relation.client.avatar"
      :sm="!md && !lg"
      :md="md"
      :lg="lg"
      class="tw-shrink-0"
      data-test="client-chip-logo"
    />

    <WdsStack direction-column class="tw-min-w-0">
      <WdsStack v-if="md || lg" align-center gap-xs>
        <WdsIcon name="building" :size="14" class="tw-text-gray-700" />
        <WdsText secondary class="tw-uppercase">
          <span class="tw-font-semibold">{{ $t('Client') }}</span>
        </WdsText>
      </WdsStack>

      <WdsHeading
        v-if="md"
        :as="static ? 'h2' : 'div'"
        type="title3"
        class="chip-name tw-line-clamp-2"
        :data-test="`${dataTest}-name`"
      >
        {{ relation.client.name }}
      </WdsHeading>
      <span v-else-if="lg" class="chip-name tw-line-clamp-2" :data-test="`${dataTest}-name`">
        {{ relation.client.name }}
      </span>
      <WdsStack v-else align-center gap-xs class="tw-min-w-0">
        <WdsText
          :text="relation.client.name"
          class="chip-name tw-line-clamp-1"
          :data-test="`${dataTest}-name`"
        />
      </WdsStack>
    </WdsStack>
  </component>

  <Teleport v-if="ownDrawerMounted && relation" :to="drawerTarget">
    <ClientDrawer
      :show="ownDrawerOpen"
      :client-relation-id="clientRelationId"
      @update:show="ownDrawerOpen = $event"
      @open-page="goToClientPage"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, inject, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsHeading, WdsIcon, WdsStack, WdsText } from '@worksome/wds-components'
import { graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { TableScope } from '@/components/GraphqlDataTable/injection'
import { openClientDetailPanel } from '@/components/GraphqlDataTable/listners'
import { useFullscreenTarget } from '@/composables/useFullscreenTarget'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import AvatarWithFallback from '@/components/AvatarWithFallback.vue'
import type { PeopleSegment } from '@/components/Clients/clientPageTabs'
import { clientPeopleUrl } from '@/components/Clients/People/peopleLinks'

// Loaded on demand and not imported statically: the drawer renders this chip in its own header.
const ClientDrawer = defineAsyncComponent(() =>
  import('@/components/Clients/ClientDrawer.vue').then((module) => module.default),
)

// Hosts spread this into their own queries so the chip's lookup is answered from the cache.
const ClientChipFields = graphql(`
  fragment ClientChip on ClientRelation {
    id
    client {
      id
      name
      avatar
    }
  }
`)

const GET_CLIENT_CHIP = graphql(`
  query ClientChip($id: ID!) {
    clientRelation(id: $id) {
      ...ClientChip
    }
  }
`)

const props = withDefaults(
  defineProps<{
    clientRelationId: string
    md?: boolean
    lg?: boolean
    static?: boolean
    dataTest?: string
  }>(),
  { md: false, lg: false, static: false, dataTest: 'client-chip' },
)

const { result } = useQuery(GET_CLIENT_CHIP, () => ({ id: props.clientRelationId }), {
  clientId: 'publicApi',
})

const relation = computed(() => useFragment(ClientChipFields, result.value?.clientRelation) ?? null)

// Inside a GraphqlDataTable the table hosts the drawer and listens on its scope; elsewhere the chip hosts its own.
const tableScope = inject(TableScope, null)

const drawerTarget = useFullscreenTarget()
const ownDrawerMounted = ref(false)
const ownDrawerOpen = ref(false)

function open() {
  if (props.static) return

  if (tableScope !== null) {
    openClientDetailPanel({ clientRelationId: props.clientRelationId }, tableScope)
    return
  }

  ownDrawerMounted.value = true
  ownDrawerOpen.value = true
}

function goToClientPage(companySupplierId: string, segment?: PeopleSegment) {
  window.location.assign(
    segment
      ? clientPeopleUrl(companySupplierId, segment)
      : laravelNamedRoute(namedRoutes.frontend.clients.view, { companySupplier: companySupplierId }),
  )
}
</script>

<style scoped lang="scss">
.client-chip {
  @apply tw-flex tw-items-center tw-gap-3 tw-min-w-0 tw-text-left;
  @apply tw-p-0 tw-border-0 tw-bg-transparent;
  color: inherit;

  // Padding is pulled back with negative margins so the highlight does not shift the layout.
  &--interactive {
    @apply tw-cursor-pointer tw-rounded tw-px-1 tw-py-0.5 -tw-mx-1 -tw-my-0.5;
    @apply hover:tw-bg-true-gray-300 focus-visible:tw-bg-true-gray-300;

    &:hover .chip-name {
      @apply tw-underline;
    }
  }
}
</style>
